import React, { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { 
  ArrowLeft, 
  Search, 
  BookOpen, 
  MessageCircle, 
  Phone, 
  Mail,
  ChevronRight,
  ExternalLink,
  CheckCircle,
  AlertCircle,
  Info,
  Clock,
  User,
  Tag,
  ThumbsUp,
  ThumbsDown,
  Eye,
  Share2
} from 'lucide-react'

const HelpDetail: React.FC = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [faq, setFaq] = useState(null)
  const [loading, setLoading] = useState(true)
  const [relatedFaqs, setRelatedFaqs] = useState([])
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false)

  // 从API获取FAQ详情
  useEffect(() => {
    const fetchFaqDetail = async () => {
      setLoading(true)
      try {
        // 尝试从API获取数据
        const response = await fetch(`/api/faq/${id}`)
        if (response.ok) {
          const data = await response.json()
          setFaq(data.data)
          
          // 获取相关FAQ
          const relatedResponse = await fetch(`/api/faq/${id}/related`)
          if (relatedResponse.ok) {
            const relatedData = await relatedResponse.json()
            setRelatedFaqs(relatedData.data || [])
          }
        } else {
          // 如果API失败，使用模拟数据
          const mockFaq = {
            _id: id,
            title: '如何开始使用CoreTrust手机？',
            content: `CoreTrust手机开箱即用，首次开机后按照引导完成设置即可。以下是详细的设置步骤：

## 1. 初始设置
- 长按电源键开机
- 选择语言和地区
- 连接WiFi网络
- 创建或登录DeepLink账户

## 2. 钱包设置
- 创建新钱包或导入现有钱包
- 设置钱包密码（建议使用强密码）
- 备份助记词（12或24个单词）
- 验证助记词备份

## 3. 生物识别设置
- 设置指纹识别
- 设置面部识别（可选）
- 测试生物识别功能

## 4. 应用下载
- 访问CoreTrust应用市场
- 下载常用DeFi应用
- 配置应用权限
- 测试应用功能

## 注意事项
- 确保在安全环境下进行设置
- 妥善保管助记词
- 定期更新系统和应用
- 启用安全功能`,
            helpful: 15,
            views: 234,
            lastUpdated: '2024-01-15',
            tags: ['新手', '设置', '钱包'],
            author: 'DeepLink Support',
            category: 'getting-started'
          }
          
          setFaq(mockFaq)
          setRelatedFaqs([])
        }
      } catch (error) {
        console.error('获取FAQ详情失败:', error)
        setFaq(null)
        setRelatedFaqs([])
      }
      setLoading(false)
    }

    if (id) {
      fetchFaqDetail()
    }
  }, [id])

  // 处理反馈提交
  const handleFeedback = async (helpful: boolean) => {
    try {
      const response = await fetch(`/api/faq/${id}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ helpful })
      })

      if (response.ok) {
        setFeedbackSubmitted(true)
        // 更新本地状态
        if (faq) {
          setFaq({
            ...faq,
            helpful: helpful ? faq.helpful + 1 : faq.helpful,
            notHelpful: helpful ? faq.notHelpful : (faq.notHelpful || 0) + 1
          })
        }
      }
    } catch (error) {
      console.error('提交反馈失败:', error)
    }
  }

  // 格式化内容为HTML
  const formatContent = (content: string) => {
    return content
      .replace(/## (.*)/g, '<h2 class="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">$1</h2>')
      .replace(/### (.*)/g, '<h3 class="text-lg font-medium text-gray-800 dark:text-gray-200 mb-2 mt-4">$1</h3>')
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-gray-900 dark:text-white">$1</strong>')
      .replace(/\*(.*?)\*/g, '<em class="italic text-gray-700 dark:text-gray-300">$1</em>')
      .replace(/^- (.*)/gm, '<li class="ml-4 mb-1">$1</li>')
      .replace(/\n\n/g, '</p><p class="mb-4">')
      .replace(/^(?!<[h|l])/gm, '<p class="mb-4 text-gray-700 dark:text-gray-300">')
      .replace(/(<li.*<\/li>)/g, '<ul class="list-disc list-inside mb-4 space-y-1">$1</ul>')
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">加载中...</p>
        </div>
      </div>
    )
  }

  if (!faq) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">FAQ不存在</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">您查找的FAQ可能已被删除或不存在</p>
          <button
            onClick={() => navigate('/help')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            返回帮助中心
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>{faq.title} - DeepLink帮助中心</title>
        <meta name="description" content={faq.content.substring(0, 160)} />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="container mx-auto px-4 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => navigate('/help')}
                  className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  返回帮助中心
                </button>
                <div className="h-6 w-px bg-gray-300 dark:bg-gray-600"></div>
                <div className="flex items-center space-x-2">
                  <BookOpen className="w-5 h-5 text-blue-500" />
                  <span className="text-sm text-gray-500 dark:text-gray-400">帮助中心</span>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <button className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
                  <Search className="w-5 h-5 mr-2" />
                  搜索
                </button>
                <button className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
                  <Share2 className="w-5 h-5 mr-2" />
                  分享
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-3">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700"
              >
                <div className="p-8">
                  {/* FAQ Header */}
                  <div className="mb-8">
                    <div className="flex items-start justify-between mb-4">
                      <h1 className="text-3xl font-bold text-gray-900 dark:text-white leading-tight">
                        {faq.title}
                      </h1>
                    </div>
                    
                    <div className="flex items-center space-x-6 text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex items-center">
                        <User className="w-4 h-4 mr-1" />
                        {faq.author || 'DeepLink Support'}
                      </div>
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {new Date(faq.lastUpdated).toLocaleDateString('zh-CN')}
                      </div>
                      <div className="flex items-center">
                        <Eye className="w-4 h-4 mr-1" />
                        {faq.views} 次浏览
                      </div>
                    </div>

                    {/* Tags */}
                    {faq.tags && faq.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-4">
                        {faq.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-sm rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* FAQ Content */}
                  <div className="prose prose-lg max-w-none">
                    <div 
                      className="text-gray-700 dark:text-gray-300 leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: formatContent(faq.content) }}
                    />
                  </div>

                  {/* Feedback Section */}
                  <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      这篇文章对您有帮助吗？
                    </h3>
                    
                    {!feedbackSubmitted ? (
                      <div className="flex items-center space-x-4">
                        <button
                          onClick={() => handleFeedback(true)}
                          className="flex items-center px-4 py-2 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
                        >
                          <ThumbsUp className="w-4 h-4 mr-2" />
                          有帮助 ({faq.helpful || 0})
                        </button>
                        <button
                          onClick={() => handleFeedback(false)}
                          className="flex items-center px-4 py-2 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                        >
                          <ThumbsDown className="w-4 h-4 mr-2" />
                          没帮助 ({faq.notHelpful || 0})
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center text-green-600 dark:text-green-400">
                        <CheckCircle className="w-5 h-5 mr-2" />
                        感谢您的反馈！
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>

              {/* Related FAQs */}
              {relatedFaqs.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="mt-8"
                >
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                    相关文章
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {relatedFaqs.map((relatedFaq) => (
                      <Link
                        key={relatedFaq._id}
                        to={`/help/${relatedFaq._id}`}
                        className="block p-6 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow"
                      >
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                          {relatedFaq.title}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 text-sm">
                          {relatedFaq.content.substring(0, 100)}...
                        </p>
                        <div className="flex items-center mt-3 text-sm text-gray-500 dark:text-gray-400">
                          <Eye className="w-4 h-4 mr-1" />
                          {relatedFaq.views} 次浏览
                          <span className="mx-2">•</span>
                          <ThumbsUp className="w-4 h-4 mr-1" />
                          {relatedFaq.helpful} 个有用
                        </div>
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-8">
                {/* Contact Support */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    需要更多帮助？
                  </h3>
                  
                  <div className="space-y-4">
                    <a
                      href="#"
                      className="flex items-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                    >
                      <MessageCircle className="w-5 h-5 text-blue-500 mr-3" />
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">在线客服</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">7x24小时在线支持</div>
                      </div>
                    </a>
                    
                    <a
                      href="tel:+85212345678"
                      className="flex items-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
                    >
                      <Phone className="w-5 h-5 text-green-500 mr-3" />
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">电话支持</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">+852 1234-5678</div>
                      </div>
                    </a>
                    
                    <a
                      href="mailto:support@deeplink.hk"
                      className="flex items-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
                    >
                      <Mail className="w-5 h-5 text-purple-500 mr-3" />
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">邮件支持</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">support@deeplink.hk</div>
                      </div>
                    </a>
                  </div>
                </div>

                {/* Quick Links */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    快速链接
                  </h3>
                  
                  <div className="space-y-3">
                    <Link
                      to="/help"
                      className="flex items-center text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      <ChevronRight className="w-4 h-4 mr-2" />
                      所有帮助文章
                    </Link>
                    <Link
                      to="/docs"
                      className="flex items-center text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      <ChevronRight className="w-4 h-4 mr-2" />
                      技术文档
                    </Link>
                    <Link
                      to="/community"
                      className="flex items-center text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      <ChevronRight className="w-4 h-4 mr-2" />
                      社区论坛
                    </Link>
                    <Link
                      to="/feedback"
                      className="flex items-center text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      <ChevronRight className="w-4 h-4 mr-2" />
                      意见反馈
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default HelpDetail